import { z } from "zod";

import { ModelPricingSchema, type ModelPricing } from "@quovy/usage";

import { AGENT_MODEL_REGISTRY, normalizeAgentModelId } from "./agent-models";

// ---------------------------------------------------------------------------
// Deployment-aware model provider routing.
//
// A *gateway* is a configured way to reach models; a *route* binds one
// logical catalog model to one gateway. Persisted model ids stay logical
// (`claude-haiku-4-5`) everywhere — gateway specifics (remote model ids,
// endpoints, credentials) live only in deployment configuration, so the
// same DB rows work across hosted and BYOC deployments.
//
// This module is pure (no env/fs access at module load) so the web bundle
// can import the types and resolution helpers. Loading the config from the
// environment lives in `@quovy/agents` (`model-routing.ts`).
//
// Exec plan: docs/exec-plans/active/deployment-aware-model-providers.md
// ---------------------------------------------------------------------------

export const DIRECT_MODEL_GATEWAY_TYPES = [
  "anthropic-direct",
  "openai-direct",
  "google-direct",
  "xai-direct",
] as const;

// Cloud/self-hosted gateways. Routes through these carry a `remoteModelId`
// (Bedrock inference profile, Azure deployment name) and default their
// capabilities OFF — provider-native server tools don't execute through them.
export const CLOUD_MODEL_GATEWAY_TYPES = [
  "bedrock",
  "azure-openai",
  "azure-foundry",
  "openai-compatible",
] as const;

export const MODEL_GATEWAY_TYPES = [
  ...DIRECT_MODEL_GATEWAY_TYPES,
  ...CLOUD_MODEL_GATEWAY_TYPES,
] as const;

export type DirectModelGatewayType = (typeof DIRECT_MODEL_GATEWAY_TYPES)[number];
export type ModelGatewayType = (typeof MODEL_GATEWAY_TYPES)[number];
export type ComputerUseToolKind = "anthropic" | "openai";

export function isDirectGatewayType(type: ModelGatewayType): type is DirectModelGatewayType {
  return (DIRECT_MODEL_GATEWAY_TYPES as readonly string[]).includes(type);
}

/**
 * Resolve the provider-native computer tool protocol a route can carry.
 *
 * A capability boolean is not enough: the OpenAI and Anthropic tools have
 * different wire formats, and cloud gateways can host models from several
 * providers. In particular, a logical OpenAI model routed to Amazon Nova is
 * not made OpenAI-computer-compatible by naming the OpenAI computer tool.
 * Keep this pure so deployment routing, workspace routing, and call-time tool
 * construction all share the same compatibility decision.
 */
export function resolveComputerUseToolKind(input: {
  catalogProvider: string;
  catalogSupportsComputerUse: boolean;
  gatewayType: ModelGatewayType;
  gatewayProtocol?: "openai-compatible" | "anthropic" | undefined;
  configuredTool?: ComputerUseToolKind | null | undefined;
}): ComputerUseToolKind | null {
  if (!input.catalogSupportsComputerUse) return null;

  let transportedProvider: ComputerUseToolKind | null = null;
  switch (input.gatewayType) {
    case "anthropic-direct":
      transportedProvider = "anthropic";
      break;
    case "openai-direct":
    case "azure-openai":
      transportedProvider = "openai";
      break;
    case "bedrock":
      // Bedrock carries Anthropic's provider tools when the configured remote
      // model is Claude. Cloud routes require an explicit configuredTool below,
      // so an opaque provisioned-model ARN does not need brittle id inference.
      transportedProvider = "anthropic";
      break;
    case "azure-foundry":
      transportedProvider = input.gatewayProtocol === "anthropic" ? "anthropic" : null;
      break;
    case "google-direct":
    case "xai-direct":
    case "openai-compatible":
      transportedProvider = null;
      break;
  }

  // Direct provider routes inherit their native tool. Cloud and proxy routes
  // default off and must name the protocol explicitly; null explicitly turns
  // a direct route off as well.
  const configuredTool =
    input.configuredTool === undefined && isDirectGatewayType(input.gatewayType)
      ? transportedProvider
      : (input.configuredTool ?? null);
  return configuredTool === transportedProvider && configuredTool === input.catalogProvider
    ? configuredTool
    : null;
}

const DIRECT_GATEWAY_TYPE_INFO: Record<
  DirectModelGatewayType,
  { label: string; locator: string; defaultApiKeyEnv: string; provider: string }
> = {
  "anthropic-direct": {
    label: "Direct — Anthropic",
    locator: "api.anthropic.com",
    defaultApiKeyEnv: "ANTHROPIC_API_KEY",
    provider: "anthropic",
  },
  "openai-direct": {
    label: "Direct — OpenAI",
    locator: "api.openai.com",
    defaultApiKeyEnv: "OPENAI_API_KEY",
    provider: "openai",
  },
  "google-direct": {
    label: "Direct — Google",
    locator: "generativelanguage.googleapis.com",
    defaultApiKeyEnv: "GOOGLE_GENERATIVE_AI_API_KEY",
    provider: "google",
  },
  "xai-direct": {
    label: "Direct — xAI",
    locator: "api.x.ai",
    defaultApiKeyEnv: "XAI_API_KEY",
    provider: "xai",
  },
};

/**
 * The env var the gateway's API key is read from when the config doesn't
 * name one. `null` for gateway types that don't authenticate with a single
 * API key (bedrock uses the AWS credential chain; openai-compatible
 * endpoints may be keyless).
 */
export function defaultApiKeyEnvForGatewayType(type: ModelGatewayType): string | null {
  if (isDirectGatewayType(type)) return DIRECT_GATEWAY_TYPE_INFO[type].defaultApiKeyEnv;
  if (type === "azure-openai" || type === "azure-foundry") return "AZURE_API_KEY";
  return null;
}

// --- Gateway config schemas -------------------------------------------------
// Secrets are referenced by env var NAME (never inlined) so a mounted config
// file stays safe to log and ship in a ConfigMap.

const directGatewayShape = {
  apiKeyEnv: z.string().min(1).optional(),
};

const bedrockGatewaySchema = z.object({
  type: z.literal("bedrock"),
  region: z.string().min(1),
  // Explicit static credentials by env var name. When omitted, the AWS
  // default credential chain applies (profile, IRSA, instance role) — the
  // normal mode in EKS.
  accessKeyIdEnv: z.string().min(1).optional(),
  secretAccessKeyEnv: z.string().min(1).optional(),
  sessionTokenEnv: z.string().min(1).optional(),
});

const azureOpenAiGatewaySchema = z
  .object({
    type: z.literal("azure-openai"),
    resourceName: z.string().min(1).optional(),
    baseURL: z.string().url().optional(),
    apiVersion: z.string().min(1).optional(),
    apiKeyEnv: z.string().min(1).optional(),
  })
  .superRefine((gateway, ctx) => {
    if (!gateway.resourceName && !gateway.baseURL) {
      ctx.addIssue({
        code: "custom",
        message: "azure-openai gateway requires resourceName or baseURL",
      });
    }
  });

const azureFoundryGatewaySchema = z.object({
  type: z.literal("azure-foundry"),
  baseURL: z.string().url(),
  // Which API shape the Foundry endpoint speaks for the routed models.
  // Claude on Foundry speaks the Anthropic Messages API; most other Foundry
  // serverless deployments are OpenAI-compatible. Verify per model against a
  // real Foundry resource (exec plan M3 caveat).
  protocol: z.enum(["openai-compatible", "anthropic"]),
  apiKeyEnv: z.string().min(1).optional(),
});

const openAiCompatibleGatewaySchema = z.object({
  type: z.literal("openai-compatible"),
  baseURL: z.string().url(),
  // Optional: self-hosted endpoints (vLLM, Ollama) are often keyless.
  apiKeyEnv: z.string().min(1).optional(),
});

const gatewayConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("anthropic-direct"), ...directGatewayShape }),
  z.object({ type: z.literal("openai-direct"), ...directGatewayShape }),
  z.object({ type: z.literal("google-direct"), ...directGatewayShape }),
  z.object({ type: z.literal("xai-direct"), ...directGatewayShape }),
  bedrockGatewaySchema,
  azureOpenAiGatewaySchema,
  azureFoundryGatewaySchema,
  openAiCompatibleGatewaySchema,
]);

export type ModelGatewayConfig = z.infer<typeof gatewayConfigSchema>;

const routeConfigSchema = z
  .object({
    gateway: z.string().min(1),
    // Model id sent over the wire to the gateway. Defaults to the logical id
    // for direct gateways; REQUIRED for cloud gateways (Bedrock inference
    // profile, Azure deployment name) — enforced in the config superRefine.
    remoteModelId: z.string().min(1).optional(),
    // Web search defaults to true for direct gateways and false for cloud
    // gateways. Computer use names its provider-native wire protocol instead of
    // using a boolean: omit for the direct-gateway default, set null to disable,
    // or name the verified protocol on a cloud route.
    supportsWebSearch: z.boolean().optional(),
    computerUseTool: z.enum(["anthropic", "openai"]).nullable().optional(),
    pricing: ModelPricingSchema.optional(),
    // Parse the removed key only so upgrades fail with actionable remediation
    // instead of silently stripping it and disabling computer use.
    supportsComputerUse: z.unknown().optional(),
  })
  .superRefine((route, ctx) => {
    if (Object.hasOwn(route, "supportsComputerUse")) {
      ctx.addIssue({
        code: "custom",
        path: ["supportsComputerUse"],
        message:
          "supportsComputerUse was replaced by computerUseTool. Set computerUseTool to openai or anthropic (or null to disable computer use), remove supportsComputerUse, then retry.",
      });
    }
  })
  .transform(({ supportsComputerUse: _removed, ...route }) => route);

export const SEARCH_EMBEDDING_DIMENSIONS = 1536;

const embeddingRouteConfigSchema = z.object({
  gateway: z.string().min(1),
  // The provider-native model identifier (Bedrock/OpenAI) or deployment name
  // (Azure OpenAI) sent over the wire. This is deliberately named the same as
  // generation routes so an Azure deployment alias cannot be mistaken for a
  // Quovy logical model id.
  remoteModelId: z.string().min(1),
  // Search storage is currently pgvector(1536). Supporting another size is
  // a schema migration plus a full re-embedding job, not a routing toggle.
  dimensions: z.literal(SEARCH_EMBEDDING_DIMENSIONS),
});

export type EmbeddingRouteConfig = z.infer<typeof embeddingRouteConfigSchema>;

export const modelProviderConfigSchema = z
  .object({
    gateways: z.record(z.string().min(1), gatewayConfigSchema),
    routes: z.record(z.string().min(1), routeConfigSchema),
    embeddings: embeddingRouteConfigSchema.optional(),
  })
  .superRefine((config, ctx) => {
    for (const [modelId, route] of Object.entries(config.routes)) {
      if (!(normalizeAgentModelId(modelId) in AGENT_MODEL_REGISTRY.models)) {
        ctx.addIssue({
          code: "custom",
          path: ["routes", modelId],
          message: `Unknown model id "${modelId}" — routes must reference models in AGENT_MODEL_REGISTRY`,
        });
      }
      const gateway = config.gateways[route.gateway];
      if (!gateway) {
        ctx.addIssue({
          code: "custom",
          path: ["routes", modelId, "gateway"],
          message: `Route references undeclared gateway "${route.gateway}"`,
        });
        continue;
      }
      if (!isDirectGatewayType(gateway.type) && !route.remoteModelId) {
        ctx.addIssue({
          code: "custom",
          path: ["routes", modelId, "remoteModelId"],
          message: `Routes through "${gateway.type}" gateways require remoteModelId (inference profile / deployment name)`,
        });
      }
      if (route.computerUseTool) {
        const entry = (
          AGENT_MODEL_REGISTRY.models as Record<
            string,
            { provider: string; supportsComputerUse?: boolean } | undefined
          >
        )[normalizeAgentModelId(modelId)];
        const toolKind = entry
          ? resolveComputerUseToolKind({
              catalogProvider: entry.provider,
              catalogSupportsComputerUse: entry.supportsComputerUse === true,
              gatewayType: gateway.type,
              gatewayProtocol: gateway.type === "azure-foundry" ? gateway.protocol : undefined,
              configuredTool: route.computerUseTool,
            })
          : null;
        if (!toolKind) {
          ctx.addIssue({
            code: "custom",
            path: ["routes", modelId, "computerUseTool"],
            message:
              `Route cannot use the "${route.computerUseTool}" computer tool through ` +
              `${gateway.type} for catalog provider "${entry?.provider ?? "unknown"}". ` +
              "Choose openai for OpenAI/Azure OpenAI, anthropic for Anthropic/Anthropic-on-Bedrock or Foundry, or null to disable computer use, then retry.",
          });
        }
      }
    }

    if (config.embeddings) {
      const gateway = config.gateways[config.embeddings.gateway];
      if (!gateway) {
        ctx.addIssue({
          code: "custom",
          path: ["embeddings", "gateway"],
          message: `Embedding route references undeclared gateway "${config.embeddings.gateway}"`,
        });
      } else if (
        gateway.type === "anthropic-direct" ||
        gateway.type === "xai-direct" ||
        (gateway.type === "azure-foundry" && gateway.protocol === "anthropic")
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["embeddings", "gateway"],
          message:
            `Gateway "${config.embeddings.gateway}" (${gateway.type}) does not support embeddings. ` +
            "Use an OpenAI, Azure OpenAI, Bedrock, Google, or OpenAI-compatible gateway.",
        });
      }
    }
  });

export type ModelProviderConfig = z.infer<typeof modelProviderConfigSchema>;

// --- Intelligence tiers ------------------------------------------------------
// The named quality levels behind `tier:*` model references. Chains are the
// CODE fallbacks — the platform model-policy document overrides them without
// a deploy (see model-policy.ts). Defined here (not model-policy.ts) so the
// subsystem defaults below can share them without an import cycle.

export const MODEL_TIERS = ["fast", "thinking", "pro"] as const;
export type ModelTier = (typeof MODEL_TIERS)[number];

/** Reserved for future heuristic routing — rejected as a tier name today. */
export const RESERVED_MODEL_TIERS = ["auto"] as const;

export const MODEL_TIER_LABELS: Record<ModelTier, string> = {
  fast: "Fast",
  thinking: "Thinking",
  pro: "Pro",
};

/**
 * Code-level fallback chains, used whenever the platform document is absent
 * or omits a tier. A fresh deployment therefore needs no seeding step.
 */
export const DEFAULT_TIER_CHAINS = {
  fast: ["gpt-5.6-luna", "gemini-3.8-flash", "claude-haiku-4-5"],
  thinking: ["gpt-5.6-terra", "claude-sonnet-5", "gemini-3.8-flash"],
  pro: ["claude-opus-5-5", "gpt-5.5-pro", "claude-opus-4-8"],
} as const satisfies Record<ModelTier, readonly string[]>;

// --- Subsystem defaults ------------------------------------------------------
// Since workspace-model-policy M5, each subsystem's default is a TIER — the
// tier's chain is the single source of fallback ordering (was: a bespoke
// per-subsystem chain). Per-workspace subsystem overrides live in the
// model-policy document's `subsystems` key as tier-or-model references.

export type SubsystemModelDefault = {
  label: string;
  tier: ModelTier;
  chain: readonly string[];
  note: string;
};

export const SUBSYSTEM_MODEL_DEFAULTS = {
  agents: {
    label: "Agents & chat titles",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Used for new agents and thread titles; each agent stores its own model",
  },
  triage: {
    label: "Triage",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Used when the workspace policy has no extractionPrimary reference",
  },
  classification: {
    label: "Classification",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Per-classifier overrides live on classifier config",
  },
  extractionPrimary: {
    label: "Extraction orchestrator",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Used when the run config's primary model has no route here",
  },
  extractionSubagent: {
    label: "Extraction subagent",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Used when the run config carries no subagent model",
  },
  extractionSummary: {
    label: "Extraction summary",
    tier: "fast",
    chain: DEFAULT_TIER_CHAINS.fast,
    note: "Document summary step inside extraction runs",
  },
} as const satisfies Record<string, SubsystemModelDefault>;

export type SubsystemModelDefaultKey = keyof typeof SUBSYSTEM_MODEL_DEFAULTS;

/**
 * The deployment config used when no explicit `MODEL_PROVIDER_CONFIG` is
 * provided: every direct gateway is declared (whether or not its key is set
 * — `credentialed` carries that signal) and every catalog model routes to
 * its provider's gateway. This reproduces pre-routing behavior exactly.
 */
export function buildImplicitModelProviderConfig(): ModelProviderConfig {
  const gateways: ModelProviderConfig["gateways"] = {
    anthropic: { type: "anthropic-direct" },
    openai: { type: "openai-direct" },
    google: { type: "google-direct" },
    xai: { type: "xai-direct" },
  };
  const routes: ModelProviderConfig["routes"] = {};
  for (const [modelId, entry] of Object.entries(AGENT_MODEL_REGISTRY.models)) {
    routes[modelId] = { gateway: entry.provider };
  }
  return { gateways, routes };
}

// --- Resolution ---------------------------------------------------------------

export type ResolvedModelGateway = {
  name: string;
  type: ModelGatewayType;
  label: string;
  /** Non-sensitive endpoint identifier (API host, region, endpoint host). */
  locator: string | null;
  /**
   * Env var the API key is read from, or null for gateways that don't
   * authenticate with a single key (bedrock credential chain, keyless
   * openai-compatible endpoints).
   */
  apiKeyEnv: string | null;
  /** Whether credentials appear present in the given env. */
  credentialed: boolean;
  /** The raw gateway config (env var names + endpoints only — no secrets). */
  config: ModelGatewayConfig;
};

export type ResolvedModelRoute = {
  modelId: string;
  label: string;
  provider: string;
  gatewayName: string;
  gatewayType: ModelGatewayType;
  gatewayLabel: string;
  locator: string | null;
  remoteModelId: string;
  credentialed: boolean;
  /** Provider-native computer tool this model/gateway route can carry. */
  computerUseTool: ComputerUseToolKind | null;
  capabilities: { webSearch: boolean; computerUse: boolean };
  /** Route-level price card; replaces the catalog card when present. */
  pricing?: ModelPricing;
};

export type ResolvedEmbeddingRoute = {
  gatewayName: string;
  gatewayType: ModelGatewayType;
  gatewayLabel: string;
  locator: string | null;
  remoteModelId: string;
  dimensions: typeof SEARCH_EMBEDDING_DIMENSIONS;
  credentialed: boolean;
};

function hostFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

// Ambient AWS credential signals the default chain can resolve from. Not
// exhaustive (the chain also reads config files), but covers profiles, IRSA,
// ECS task roles, and static env keys — good enough for the Control Plane
// credential indicator.
const AWS_AMBIENT_CREDENTIAL_ENVS = [
  "AWS_ACCESS_KEY_ID",
  "AWS_PROFILE",
  "AWS_ROLE_ARN",
  "AWS_WEB_IDENTITY_TOKEN_FILE",
  "AWS_CONTAINER_CREDENTIALS_RELATIVE_URI",
];

export const AMBIENT_IDENTITY_GATEWAY_TYPES = ["bedrock", "azure-openai", "azure-foundry"] as const;

export type AmbientIdentityGatewayType = (typeof AMBIENT_IDENTITY_GATEWAY_TYPES)[number];

export function gatewayTypeAllowsAmbientIdentity(
  type: ModelGatewayType,
): type is AmbientIdentityGatewayType {
  return (AMBIENT_IDENTITY_GATEWAY_TYPES as readonly string[]).includes(type);
}

export function hasAwsAmbientCredentials(env: Record<string, string | undefined>): boolean {
  return AWS_AMBIENT_CREDENTIAL_ENVS.some((key) => Boolean(env[key]?.trim()));
}

/**
 * Ambient Azure credential signals DefaultAzureCredential can resolve.
 * Covers AKS workload identity (federated token file), managed identity,
 * and a client-secret service principal. AZURE_CLIENT_ID alone is not
 * enough — it is also injected on workloads that only talk to Blob.
 */
export function hasAzureAmbientCredentials(env: Record<string, string | undefined>): boolean {
  if (env.AZURE_FEDERATED_TOKEN_FILE?.trim()) return true;
  if (env.IDENTITY_ENDPOINT?.trim() || env.MSI_ENDPOINT?.trim()) return true;
  return Boolean(env.AZURE_CLIENT_ID?.trim() && env.AZURE_CLIENT_SECRET?.trim());
}

function resolveAzureGatewayCredentials(
  gateway: Extract<ModelGatewayConfig, { type: "azure-openai" | "azure-foundry" }>,
  env: Record<string, string | undefined>,
): { apiKeyEnv: string | null; credentialed: boolean } {
  const explicitEnv = gateway.apiKeyEnv;
  if (explicitEnv) {
    return { apiKeyEnv: explicitEnv, credentialed: Boolean(env[explicitEnv]?.trim()) };
  }
  if (env.AZURE_API_KEY?.trim()) {
    return { apiKeyEnv: "AZURE_API_KEY", credentialed: true };
  }
  if (hasAzureAmbientCredentials(env)) {
    return { apiKeyEnv: null, credentialed: true };
  }
  return { apiKeyEnv: "AZURE_API_KEY", credentialed: false };
}

function resolveGateway(
  name: string,
  gateway: ModelGatewayConfig,
  env: Record<string, string | undefined>,
): ResolvedModelGateway {
  if (isDirectGatewayType(gateway.type)) {
    const info = DIRECT_GATEWAY_TYPE_INFO[gateway.type];
    const apiKeyEnv =
      ("apiKeyEnv" in gateway ? gateway.apiKeyEnv : undefined) ?? info.defaultApiKeyEnv;
    return {
      name,
      type: gateway.type,
      label: info.label,
      locator: info.locator,
      apiKeyEnv,
      credentialed: Boolean(env[apiKeyEnv]?.trim()),
      config: gateway,
    };
  }

  switch (gateway.type) {
    case "bedrock": {
      const explicitEnv = gateway.accessKeyIdEnv ?? null;
      const credentialed = explicitEnv
        ? Boolean(env[explicitEnv]?.trim())
        : hasAwsAmbientCredentials(env);
      return {
        name,
        type: gateway.type,
        label: "Amazon Bedrock",
        locator: gateway.region,
        apiKeyEnv: explicitEnv,
        credentialed,
        config: gateway,
      };
    }
    case "azure-openai": {
      const azure = resolveAzureGatewayCredentials(gateway, env);
      return {
        name,
        type: gateway.type,
        label: "Azure OpenAI",
        locator: gateway.resourceName
          ? `${gateway.resourceName}.openai.azure.com`
          : hostFromUrl(gateway.baseURL),
        apiKeyEnv: azure.apiKeyEnv,
        credentialed: azure.credentialed,
        config: gateway,
      };
    }
    case "azure-foundry": {
      const azure = resolveAzureGatewayCredentials(gateway, env);
      return {
        name,
        type: gateway.type,
        label: "Azure AI Foundry",
        locator: hostFromUrl(gateway.baseURL),
        apiKeyEnv: azure.apiKeyEnv,
        credentialed: azure.credentialed,
        config: gateway,
      };
    }
    case "openai-compatible": {
      const apiKeyEnv = gateway.apiKeyEnv ?? null;
      return {
        name,
        type: gateway.type,
        label: "OpenAI-compatible endpoint",
        locator: hostFromUrl(gateway.baseURL),
        apiKeyEnv,
        // Keyless endpoints are valid (self-hosted vLLM behind a network
        // boundary); only report missing credentials when a key env var was
        // explicitly configured but is unset.
        credentialed: apiKeyEnv ? Boolean(env[apiKeyEnv]?.trim()) : true,
        config: gateway,
      };
    }
  }
}

export function resolveModelGateways(
  config: ModelProviderConfig,
  env: Record<string, string | undefined>,
): ResolvedModelGateway[] {
  return Object.entries(config.gateways).map(([name, gateway]) =>
    resolveGateway(name, gateway, env),
  );
}

export function resolveModelRoutes(
  config: ModelProviderConfig,
  env: Record<string, string | undefined>,
): ResolvedModelRoute[] {
  const gateways = new Map(resolveModelGateways(config, env).map((g) => [g.name, g]));
  const catalog = AGENT_MODEL_REGISTRY.models as Record<
    string,
    { label: string; provider: string; supportsWebSearch?: boolean; supportsComputerUse?: boolean }
  >;

  const routes: ResolvedModelRoute[] = [];
  // Route keys normalize through the legacy-alias map, and an alias can
  // collide with a registry key that is itself still in the catalog (e.g.
  // gemini-3.1-flash-lite-preview → gemini-3.1-flash-lite). First valid
  // route per normalized id wins; later duplicates are dropped.
  const seen = new Set<string>();
  for (const [modelId, route] of Object.entries(config.routes)) {
    const normalized = normalizeAgentModelId(modelId);
    if (seen.has(normalized)) continue;
    const entry = catalog[normalized];
    const gateway = gateways.get(route.gateway);
    // Schema validation rejects these; the guard keeps hand-built configs safe.
    if (!entry || !gateway) continue;
    seen.add(normalized);
    const computerUseTool = resolveComputerUseToolKind({
      catalogProvider: entry.provider,
      catalogSupportsComputerUse: entry.supportsComputerUse === true,
      gatewayType: gateway.type,
      gatewayProtocol:
        gateway.config.type === "azure-foundry" ? gateway.config.protocol : undefined,
      configuredTool: route.computerUseTool,
    });
    routes.push({
      modelId: normalized,
      label: entry.label,
      provider: entry.provider,
      gatewayName: gateway.name,
      gatewayType: gateway.type,
      gatewayLabel: gateway.label,
      locator: gateway.locator,
      remoteModelId: route.remoteModelId ?? normalized,
      credentialed: gateway.credentialed,
      computerUseTool,
      capabilities: {
        webSearch:
          entry.supportsWebSearch === true &&
          (route.supportsWebSearch ?? isDirectGatewayType(gateway.type)),
        computerUse: computerUseTool !== null,
      },
      ...(route.pricing ? { pricing: route.pricing } : {}),
    });
  }
  return routes;
}

export function resolveModelRoute(
  config: ModelProviderConfig,
  env: Record<string, string | undefined>,
  modelId: string,
): ResolvedModelRoute | null {
  const normalized = normalizeAgentModelId(modelId);
  return resolveModelRoutes(config, env).find((route) => route.modelId === normalized) ?? null;
}

export function resolveEmbeddingRoute(
  config: ModelProviderConfig,
  env: Record<string, string | undefined>,
): ResolvedEmbeddingRoute | null {
  if (!config.embeddings) return null;
  const gateway = resolveModelGateways(config, env).find(
    (candidate) => candidate.name === config.embeddings?.gateway,
  );
  if (!gateway) return null;
  return {
    gatewayName: gateway.name,
    gatewayType: gateway.type,
    gatewayLabel: gateway.label,
    locator: gateway.locator,
    remoteModelId: config.embeddings.remoteModelId,
    dimensions: config.embeddings.dimensions,
    credentialed: gateway.credentialed,
  };
}
